/**
 * Formats currency amounts with proper decimal places and currency symbol
 * @param amount - Amount to format
 * @param currency - Currency code (defaults to BHD)
 * @param showSymbol - Whether to show currency symbol
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number | undefined,
  currency: string = "BHD",
  showSymbol: boolean = true
): string => {
  if (amount === undefined || amount === null) {
    return "—";
  }

  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

  return showSymbol ? `${currency} ${formatted}` : formatted;
};

/**
 * Formats date strings for display
 * @param dateString - ISO date string
 * @param includeTime - Whether to include time in format
 * @returns Formatted date string
 */
export const formatDate = (
  dateString: string,
  includeTime: boolean = true
): string => {
  const date = new Date(dateString);

  if (includeTime) {
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } else {
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  }
};

/**
 * Formats relative date (e.g., "2 days ago", "1 month ago")
 * @param dateString - ISO date string
 * @returns Relative date string
 */
export const formatRelativeDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "Yesterday";
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;

  return `${Math.floor(diffInDays / 365)} years ago`;
};

/**
 * Formats account code with proper padding if needed
 * @param accountCode - Account code to format
 * @param minLength - Minimum length for padding
 * @returns Formatted account code
 */
export const formatAccountCode = (
  accountCode: string,
  minLength: number = 4
): string => {
  return accountCode.padStart(minLength, "0");
};

/**
 * Formats account display name (code + name)
 * @param accountCode - Account code
 * @param accountName - Account name
 * @param showCode - Whether to show the code
 * @returns Formatted account display string
 */
export const formatAccountDisplay = (
  accountCode: string,
  accountName: string,
  showCode: boolean = true
): string => {
  if (showCode) {
    return `${formatAccountCode(accountCode)} - ${accountName}`;
  }
  return accountName;
};

/**
 * Formats large numbers with appropriate units (K, M, B)
 * @param amount - Number to format
 * @param currency - Currency code
 * @returns Formatted number with units
 */
export const formatLargeNumber = (
  amount: number,
  currency: string = "BHD"
): string => {
  const absAmount = Math.abs(amount);

  if (absAmount >= 1000000000) {
    return `${currency} ${(amount / 1000000000).toFixed(1)}B`;
  } else if (absAmount >= 1000000) {
    return `${currency} ${(amount / 1000000).toFixed(1)}M`;
  } else if (absAmount >= 1000) {
    return `${currency} ${(amount / 1000).toFixed(1)}K`;
  } else {
    return formatCurrency(amount, currency);
  }
};

/**
 * Formats percentage values
 * @param value - Decimal value (e.g., 0.1 for 10%)
 * @param decimals - Number of decimal places
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number,
  decimals: number = 1
): string => {
  return `${(value * 100).toFixed(decimals)}%`;
};

/**
 * Formats file sizes in bytes to human readable format
 * @param bytes - Size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Truncates text to specified length with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength - 3)}...`;
};

/**
 * Capitalizes first letter of each word
 * @param text - Text to capitalize
 * @returns Capitalized text
 */
export const capitalizeWords = (text: string): string => {
  return text
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Formats phone numbers for display
 * @param phoneNumber - Phone number string
 * @returns Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, "");

  // Check if it's a Bahrain number (+973)
  if (cleaned.startsWith("973") && cleaned.length === 11) {
    return `+973 ${cleaned.slice(3, 7)} ${cleaned.slice(7)}`;
  }

  // Generic international format
  if (cleaned.length >= 10) {
    return `+${cleaned.slice(0, -10)} ${cleaned.slice(-10, -7)} ${cleaned.slice(
      -7,
      -4
    )} ${cleaned.slice(-4)}`;
  }

  return phoneNumber; // Return original if can't format
};

/**
 * Gets color for different status types
 * @param status - Status string
 * @param type - Type of status (journal, account, etc.)
 * @returns Color hex code
 */
export const getStatusColor = (
  status: string,
  type: "journal" | "account" | "general" = "general"
): string => {
  switch (type) {
    case "journal":
      switch (status.toLowerCase()) {
        case "posted":
          return "#10B981";
        case "draft":
          return "#F59E0B";
        case "reversed":
          return "#EF4444";
        default:
          return "#6B7280";
      }
    case "account":
      switch (status.toLowerCase()) {
        case "active":
          return "#10B981";
        case "inactive":
          return "#EF4444";
        default:
          return "#6B7280";
      }
    default:
      switch (status.toLowerCase()) {
        case "active":
        case "posted":
        case "completed":
        case "success":
          return "#10B981";
        case "pending":
        case "draft":
        case "warning":
          return "#F59E0B";
        case "inactive":
        case "reversed":
        case "cancelled":
        case "error":
          return "#EF4444";
        case "info":
          return "#3B82F6";
        default:
          return "#6B7280";
      }
  }
};

/**
 * Gets color for different source modules
 * @param module - Source module string
 * @returns Color hex code
 */
export const getSourceModuleColor = (module: string): string => {
  if (!module) {
    return "#6B7280";
  }

  switch (module.toLowerCase()) {
    case "pos":
      return "#10B981";
    case "procurement":
      return "#3B82F6";
    case "payroll":
      return "#8B5CF6";
    case "manual":
      return "#F59E0B";
    case "system setup":
      return "#6B7280";
    default:
      return "#6B7280";
  }
};

/**
 * Gets color for different account types
 * @param accountType - Account type string
 * @returns Color hex code
 */
export const getAccountTypeColor = (accountType: string): string => {
  switch (accountType.toLowerCase()) {
    case "asset":
      return "#10B981";
    case "liability":
      return "#EF4444";
    case "equity":
      return "#3B82F6";
    case "revenue":
      return "#8B5CF6";
    case "expense":
      return "#F59E0B";
    default:
      return "#6B7280";
  }
};
